import { AnalyticsBrowser, type EventProperties } from '@customerio/cdp-analytics-browser';

import { logger } from '../logger';

/** Normalize VITE_PUBLIC_CUSTOMERIO_WRITE_KEY (trim; strip accidental wrapping quotes). */
function readCustomerIoWriteKey(): string {
  const raw = import.meta.env.VITE_PUBLIC_CUSTOMERIO_WRITE_KEY;
  if (typeof raw !== 'string') return '';
  let key = raw.trim();
  if ((key.startsWith('"') && key.endsWith('"')) || (key.startsWith("'") && key.endsWith("'"))) {
    key = key.slice(1, -1).trim();
  }
  return key;
}

const initializeAnalytics = () => {
  const isCustomerIOEnabled = import.meta.env.VITE_CUSTOMERIO_ENABLED === 'true';

  if (!isCustomerIOEnabled) {
    return null;
  }

  try {
    const writeKey = readCustomerIoWriteKey();

    if (!writeKey) {
      throw new Error('CIO analytics is enabled but write key is not set');
    }

    // If this request 404s in the network tab, the key is wrong for CDP (typo, stale key,
    // Site ID / API key used instead of the JavaScript source write key from Data Pipelines).
    return AnalyticsBrowser.load({ writeKey });
  } catch (error) {
    logger.error(error, { message: 'Failed to initialize CIO analytics' });
    return null;
  }
};

export const cioanalytics = initializeAnalytics();

let warnedCustomerIoUnavailable = false;

/**
 * Low-level Customer.io track call. Prefer typed helpers from `@/lib/analytics`.
 */
export function trackCioEvent(eventName: string, properties?: EventProperties) {
  if (!cioanalytics) {
    if (import.meta.env.DEV && !warnedCustomerIoUnavailable) {
      warnedCustomerIoUnavailable = true;
      console.warn(
        '[analytics] Customer.io is not active (missing client). Events such as "dtc_contact_info_submitted" will not appear in CIO until VITE_CUSTOMERIO_ENABLED=true and VITE_PUBLIC_CUSTOMERIO_WRITE_KEY are set in .env, then restart the dev server. Confirm you are viewing the same CIO workspace as that write key.'
      );
    }
    return;
  }

  cioanalytics.track(eventName, properties);
}
